// Las referencias de una generación, como las ve el usuario en la card —
// ADR-2026-09-25-las-referencias-se-ven-en-la-card.
//
// El agente elige referencias por id (`reference_images`: producto, modelo, logo, una
// imagen generada, una adjunta de un mensaje anterior, una pose o un fondo guardados) y la
// card las mandaba sin mostrarlas: al editar una imagen no se veía CUÁL se iba a editar.
// Acá cada spec se vuelve una ficha (miniatura + rótulo). La parte pura (`rotulo…`,
// `fichas…`) se testea; el hook resuelve las miniaturas que piden red (una generada, una
// adjunta anterior, una pose, un fondo, el logo).

import { useEffect, useMemo, useRef, useState } from 'react';
import { preloadGeneratedImages } from '../../utils/preloadGeneratedImages';
import { fetchBrandContext } from './agentMemoryApi';
import { signReferenceImage } from './agentAttachments';
import { adjuntoPorId, type MensajeConAdjuntos } from './adjuntosDelMensaje';
import { urlDeFondoGuardado, urlDePoseGuardada } from './referenciasDelCatalogo';

export type FuenteDeReferencia = 'producto' | 'modelo' | 'logo' | 'adjunto' | 'generada' | 'pose' | 'fondo';

/** Lo que el agente manda por referencia (imagen y video comparten la forma). */
export interface SpecDeReferencia {
  source: FuenteDeReferencia;
  product_id?: string;
  model_id?: string | number;
  image_id?: string;
  pose_id?: string;
  fondo_id?: string;
  adjunto_id?: string;
  etiqueta?: string;
  imagen?: number;
}

export interface FichaDeReferencia {
  /** URL de la miniatura, si ya se sabe. Sin ella la ficha muestra sólo el rótulo. */
  thumb?: string;
  label: string;
}

export interface NombresDeReferencia {
  nombreDeProducto?: string | null;
  nombreDeModelo?: string | null;
  /** "Modelo" en Moda, "Avatar" en el resto. */
  palabraModelo: string;
}

const TOPE_ROTULO = 60;

/** La clave con la que se guarda la miniatura resuelta de una spec. */
export const claveDeReferencia = (s: SpecDeReferencia): string =>
  `${s.source}:${s.product_id ?? s.model_id ?? s.image_id ?? s.pose_id ?? s.fondo_id ?? s.adjunto_id ?? ''}`;

/** Qué dice la ficha: la etiqueta del agente si la puso; si no, lo que es por su fuente. */
export function rotuloDeReferencia(spec: SpecDeReferencia, nombres: NombresDeReferencia): string {
  const etiqueta = spec.etiqueta?.trim();
  if (etiqueta) return etiqueta.length > TOPE_ROTULO ? `${etiqueta.slice(0, TOPE_ROTULO - 1)}…` : etiqueta;
  switch (spec.source) {
    case 'producto':
      return nombres.nombreDeProducto?.trim() || 'Producto';
    case 'modelo':
      return nombres.nombreDeModelo?.trim() || nombres.palabraModelo;
    case 'logo':
      return 'Logo de la marca';
    case 'generada':
      return 'Imagen generada';
    case 'pose':
      return 'Pose guardada';
    case 'fondo':
      return 'Fondo guardado';
    case 'adjunto':
    default:
      return 'Imagen adjunta';
  }
}

/** Una spec "adjunto" SIN `adjunto_id` no es una imagen más: etiqueta una de las adjuntas de este mensaje. */
const esEtiquetaDeAdjuntaActual = (s: SpecDeReferencia): boolean => s.source === 'adjunto' && !s.adjunto_id;

/**
 * Las fichas, en el orden en que viajan: primero las adjuntas de ESTE mensaje (entran
 * solas), después lo que eligió el agente. `resueltas` trae las miniaturas que el hook ya
 * consiguió, por clave; `sincronas` las que se saben sin red (producto, modelo).
 */
export function fichasDeReferencias(p: {
  specs: SpecDeReferencia[];
  adjuntasActuales: string[];
  resueltas: Record<string, string | undefined>;
  nombres: (spec: SpecDeReferencia) => NombresDeReferencia & { thumb?: string };
}): FichaDeReferencia[] {
  const fichas: FichaDeReferencia[] = [];
  const etiquetasDeAdjuntas = p.specs.filter(esEtiquetaDeAdjuntaActual);
  p.adjuntasActuales.forEach((url, i) => {
    const spec = etiquetasDeAdjuntas.find((s) =>
      typeof s.imagen === 'number' ? s.imagen === i + 1 : i === 0 && etiquetasDeAdjuntas.length === 1,
    );
    const etiqueta = spec?.etiqueta?.trim();
    fichas.push({
      thumb: url,
      label: etiqueta || (p.adjuntasActuales.length > 1 ? `Adjunto ${i + 1}` : 'Imagen adjunta'),
    });
  });
  for (const spec of p.specs) {
    if (esEtiquetaDeAdjuntaActual(spec)) continue;
    const n = p.nombres(spec);
    fichas.push({
      thumb: n.thumb || p.resueltas[claveDeReferencia(spec)] || undefined,
      label: rotuloDeReferencia(spec, n),
    });
  }
  return fichas;
}

/**
 * Las fichas de una card, con las miniaturas que piden red resueltas de a poco. Una
 * miniatura conseguida se guarda por clave en el estado del hook (la card se re-renderiza
 * seguido —el prompt se tipea— y no puede volver a firmar cada vez); una que todavía no
 * se pudo pedir (sin `companyId` aún) o que falló NO queda marcada: se vuelve a intentar
 * cuando cambian las dependencias (revisión de Greptile al PR #146).
 */
export function useMiniaturasDeReferencias(p: {
  specs: SpecDeReferencia[];
  adjuntasActuales: string[];
  mensajes: MensajeConAdjuntos[];
  companyId: string | null | undefined;
  userId: string | null | undefined;
  productos: { id: string; name: string; imageUrl: string }[];
  modelos: { id: number | string; nombre: string; thumbnailUrl: string }[];
  palabraModelo: string;
}): FichaDeReferencia[] {
  const [resueltas, setResueltas] = useState<Record<string, string | undefined>>({});
  // Las que están en vuelo ahora mismo, para no pedir dos veces la misma mientras vuelve.
  const enVueloRef = useRef(new Set<string>());
  // La identidad del array cambia en cada render (sale de `card.input`); lo que importa
  // es su contenido.
  const firma = JSON.stringify(p.specs.map(claveDeReferencia));

  useEffect(() => {
    let vivo = true;
    const necesitaEmpresa = (s: SpecDeReferencia): boolean =>
      s.source === 'generada' || s.source === 'pose' || s.source === 'logo';
    const pendientes = p.specs.filter((s) => {
      if (esEtiquetaDeAdjuntaActual(s) || s.source === 'producto' || s.source === 'modelo') return false;
      // Sin empresa todavía no se puede pedir: queda para cuando llegue (está en las deps).
      if (necesitaEmpresa(s) && !p.companyId) return false;
      const clave = claveDeReferencia(s);
      return !resueltas[clave] && !enVueloRef.current.has(clave);
    });
    if (pendientes.length === 0) return;
    for (const s of pendientes) enVueloRef.current.add(claveDeReferencia(s));
    void (async () => {
      for (const spec of pendientes) {
        const clave = claveDeReferencia(spec);
        let url: string | null = null;
        try {
          if (spec.source === 'generada' && spec.image_id && p.companyId) {
            const imgs = await preloadGeneratedImages([spec.image_id], p.companyId);
            // Una ficha de 20 px: alcanza con la versión optimizada; la completa es respaldo.
            url = imgs[0]?.optSignedUrl ?? imgs[0]?.signedUrl ?? null;
          } else if (spec.source === 'adjunto' && spec.adjunto_id) {
            const a = adjuntoPorId(p.mensajes, spec.adjunto_id);
            url = a ? await signReferenceImage(a.storagePath, a.bucket) : null;
          } else if (spec.source === 'pose' && spec.pose_id && p.companyId) {
            url = await urlDePoseGuardada(spec.pose_id, p.companyId);
          } else if (spec.source === 'fondo' && spec.fondo_id) {
            url = await urlDeFondoGuardado(spec.fondo_id, p.companyId ?? '');
          } else if (spec.source === 'logo' && p.companyId) {
            url = (await fetchBrandContext(p.userId ?? null, p.companyId))?.logoUrl ?? null;
          }
        } catch {
          url = null;
        }
        // Falló o no hubo URL: se suelta para que el próximo cambio la vuelva a pedir.
        enVueloRef.current.delete(clave);
        if (!vivo || !url) continue;
        setResueltas((prev) => ({ ...prev, [clave]: url ?? undefined }));
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, p.companyId, p.userId]);

  return useMemo(
    () =>
      fichasDeReferencias({
        specs: p.specs,
        adjuntasActuales: p.adjuntasActuales,
        resueltas,
        nombres: (spec) => {
          if (spec.source === 'producto') {
            const prod = p.productos.find((x) => String(x.id) === String(spec.product_id));
            return { nombreDeProducto: prod?.name, palabraModelo: p.palabraModelo, thumb: prod?.imageUrl };
          }
          if (spec.source === 'modelo') {
            const m = p.modelos.find((x) => String(x.id) === String(spec.model_id));
            return { nombreDeModelo: m?.nombre, palabraModelo: p.palabraModelo, thumb: m?.thumbnailUrl };
          }
          return { palabraModelo: p.palabraModelo };
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firma, p.adjuntasActuales, resueltas, p.productos, p.modelos, p.palabraModelo],
  );
}
